// read.ts — tolerant low-level readers for the status-effect renderer.
//
// The compiled status blob carries some fields as bare scalars and some still [0]-wrapped
// (cache-rollover tolerance across the statuses-bucket migration), and a few live in the
// item_layer_types bucket rather than top-level. These helpers paper over both so the renderer
// can read by key without caring which shape a given field landed in.

/** Unwrap a `[value]` array to its first element; pass scalars through untouched. */
export function scalar(v: unknown): unknown {
    return Array.isArray(v) ? v[0] : v;
}

/**
 * Read a field that may be top-level OR in the item_layer_types bucket, returning a scalar.
 * Top-level wins (that is where the status instance carries its own status_* fields); the
 * layer bucket is the fallback for layer-stored references (e.g. status_origin_id_*).
 */
export function pick(obj: Record<string, unknown> | null | undefined, key: string): unknown {
    if (!obj) return undefined;
    if (obj[key] !== undefined) return scalar(obj[key]);
    const layers = obj['item_layer_types'];
    if (layers && typeof layers === 'object' && key in (layers as Record<string, unknown>)) {
        return scalar((layers as Record<string, unknown>)[key]);
    }
    return undefined;
}

/**
 * Coerce a read value to a display string. Drops non-primitives to '' on purpose: a layer entry
 * can be a structured object ({value, …}) rather than a bare string, and "[object Object]" must
 * never reach the DOM — the caller falls back to a fetched reference label instead.
 */
export function str(v: unknown): string {
    const s = scalar(v);
    if (s == null) return '';
    if (typeof s === 'object') return '';
    return String(s);
}

/**
 * A layer/flag value is "on" when it's a present, non-empty, non-zero scalar. NOTE the '0' case:
 * a stored `0` arrives as the STRING '0' through the JSON blob, and `!!'0'` is true — so every
 * flag gate must go through here, never through `!!str(…)` (that read a stored 0 as ON).
 */
export function flagOn(v: unknown): boolean {
    const s = scalar(v);
    if (s === undefined || s === null || s === '' || s === false) return false;
    if (typeof s === 'number') return s !== 0;
    if (typeof s === 'object') return false;
    return String(s) !== '' && String(s) !== '0';
}

/** Numeric read (NaN → 0), for AOE distances and the campaign-time tuples. */
export function num(v: unknown): number {
    const n = Number(str(v));
    return Number.isFinite(n) ? n : 0;
}
